import { Form, Formik } from 'formik';
import { URLKeys, invalidateQueryExactMatch, noddiAsync } from 'noddi-async';
import {
  ApiErrorMessageInternal,
  LoadingScreen,
  NoddiBasicCard,
  NoddiCard,
  NoddiFormTextInput,
  getLongAddressName,
  useNoddiToast
} from 'noddi-ui-web';
import { useParams } from 'react-router-dom';

import { NoddiIcon } from 'noddi-ui-common';
import { commonTexts } from 'noddi-util';
import { type InferType, object, string } from 'yup';
import { NoddiButton } from '../../molecules';

const validationSchema = object({
  name: string().required('Required')
});

type FormValues = InferType<typeof validationSchema>;

export const DetailsTab = () => {
  const { id: userGroupIdString } = useParams();
  const { noddiToast } = useNoddiToast();

  const userGroupId = Number(userGroupIdString);

  const {
    data: userGroup,
    error: userGroupError,
    isPending: isUserGroupPending
  } = noddiAsync.useGet({
    type: URLKeys.getUserGroup,
    input: { id: userGroupId }
  });

  const { mutateAsync: patchUserGroup } = noddiAsync.usePatch({
    type: URLKeys.patchUserGroup,
    queryConfig: {
      onSuccess: async () => {
        invalidateQueryExactMatch({ urlKey: URLKeys.getUserGroup, input: { id: userGroupId } });
        invalidateQueryExactMatch({ urlKey: URLKeys.getGroupMembersById, input: { groupId: userGroupId } });
        noddiToast.success(commonTexts.successFullyUpdated);
      },
      onError: async () => {
        noddiToast.error('An error occurred');
      }
    }
  });

  if (isUserGroupPending) {
    return <LoadingScreen />;
  }
  if (userGroupError) {
    return <ApiErrorMessageInternal error={userGroupError} />;
  }

  return (
    <Formik<FormValues>
      initialValues={{ name: userGroup.name }}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        await patchUserGroup({
          id: userGroup.id,
          name: values.name
        });
      }}
    >
      {({ isValid, isSubmitting, submitForm }) => (
        <div className='flex flex-col gap-4'>
          <Form>
            <NoddiBasicCard>
              <NoddiFormTextInput name='name' label='Name' />
              <NoddiButton className='mt-6' disabled={!isValid} loading={isSubmitting} onClick={submitForm}>
                Update
              </NoddiButton>
            </NoddiBasicCard>
          </Form>

          <NoddiCard title='Saved addresses'>
            <div className='flex flex-col gap-3'>
              {userGroup.addresses.map((address) => (
                <div key={address.id} className='flex gap-2'>
                  <NoddiIcon name='LocationPin' />
                  <p>{getLongAddressName(address)}</p>
                </div>
              ))}
            </div>
          </NoddiCard>
        </div>
      )}
    </Formik>
  );
};
